import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { decimalToTimeString } from '../utils/filterUtils';

const TimeRangeSlider = ({ 
  label, 
  startTime, 
  endTime, 
  onTimeChange, 
  minTime = 0, 
  maxTime = 24 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragHandle, setDragHandle] = useState(null);
  const sliderRef = useRef(null);

  const handleMouseDown = (e, handle) => {
    setIsDragging(true);
    setDragHandle(handle);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(1, x / width));
    const newTime = minTime + percentage * (maxTime - minTime);

    if (dragHandle === 'start') {
      const newStartTime = Math.min(newTime, endTime - 0.5);
      onTimeChange({ start: newStartTime, end: endTime });
    } else if (dragHandle === 'end') {
      const newEndTime = Math.max(newTime, startTime + 0.5);
      onTimeChange({ start: startTime, end: newEndTime });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragHandle(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragHandle, startTime, endTime, onTimeChange, minTime, maxTime]);

  const startPercentage = ((startTime - minTime) / (maxTime - minTime)) * 100;
  const endPercentage = ((endTime - minTime) / (maxTime - minTime)) * 100;
  const rangeWidth = endPercentage - startPercentage;

  return (
    <Box sx={{ marginBottom: 3 }}>
      <Typography variant="body2" sx={{ fontWeight: 100, fontSize: 14, marginBottom: 1, color: '#222' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#666', marginBottom: 1 }}>
        <span>{decimalToTimeString(startTime)}</span>
        <span>{decimalToTimeString(endTime)}</span>
      </Box>
      <Box 
        ref={sliderRef}
        sx={{ 
          position: 'relative', 
          height: 2, 
          backgroundColor: '#e5e7eb', 
          borderRadius: 3, 
          margin: '0 10px',
          cursor: 'pointer'
        }}
      >
        {/* Range fill */}
        <Box sx={{ 
          position: 'absolute',
          left: `${startPercentage}%`,
          width: `${rangeWidth}%`,
          height: '100%',
          backgroundColor: '#3b82f6',
          borderRadius: 3
        }} />
        
        {/* Start handle */}
        <Box 
          sx={{ 
            position: 'absolute',
            left: `calc(${startPercentage}% - 10px)`,
            top: '-7px',
            width: 20,
            height: 20,
            backgroundColor: '#fff',
            border: '2px solid rgb(117, 125, 139)',
            borderRadius: '50%',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing'
            }
          }}
          onMouseDown={(e) => handleMouseDown(e, 'start')}
        />
        
        {/* End handle */}
        <Box 
          sx={{ 
            position: 'absolute',
            left: `calc(${endPercentage}% - 10px)`,
            top: '-7px',
            width: 20,
            height: 20,
            backgroundColor: '#fff',
            border: '2px solid rgb(124, 135, 152)',
            borderRadius: '50%',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing'
            }
          }}
          onMouseDown={(e) => handleMouseDown(e, 'end')}
        />
      </Box>
    </Box>
  );
};

export default TimeRangeSlider; 